import { useEffect, useState } from "react";
import { enableBillingRunningTotal, enableUpDownLineNavigation } from "../../../Utils/features";
import getClassNames from "../../../Utils/getClassNames";
import Button from "../../Simple/Button";
import Input from "../../Simple/Input";
import Select from '../../Simple/Select';

const UP_NAV_ID_ATTRIBUTE_NAME = 'data-nav-up-id';
const DOWN_NAV_ID_ATTRIBUTE_NAME = 'data-nav-down-id';

export default function EntryTable(props) {
    const [count, setCount] = useState(0);
    const [enteredCount, setEnteredCount] = useState(0);
    const [displayMaxWarning, setDisplayMaxWarning] = useState(false);

    const applyTransformIfApplicable = (name, value) => {
        const column = props.children.find(c => c.props.name === name);
        if (column.props.valueTransform) {
            value = column.props.valueTransform(value);
        }

        return value;
    }

    const handleOnChange = (name, index, value) => {
        value = applyTransformIfApplicable(name, value);
        props.collection.change(index, name, value);
    };

    const handleOnRemove = () => {
        props.collection.remove(props.collection.values.length - 1);
    }

    useEffect(() => {
        setCount(props.collection.values.length);
        setEnteredCount(props.collection.values.length);
    }, [props.collection.values]);

    useEffect(() => {
        if (!reachedMax(count)) {
            setDisplayMaxWarning(false);
        }
    }, [count]);

    const reachedMax = (value) => {
        return props.max !== undefined && props.max !== null && value >= props.max;
    };

    const isGoingToSurpassMax = (value) => {
        return props.max !== undefined && props.max !== null && value > props.max;
    };

    const handleOnAdd = () => {
        if (!reachedMax(count)) {
            props.collection.add();
        } else {
            setDisplayMaxWarning(true);
        }
    };

    const handleOnEnteredCountChange = (value) => {
        if (value > count) {
            if (isGoingToSurpassMax(value)) {
                setDisplayMaxWarning(true);
            }

            if (reachedMax(value)) {
                value = props.max;
            }

            props.collection.bulkAdd(value - count);
        } else if (value < count) {
            props.collection.bulkRemove(count - value);
        }

        setEnteredCount(value);
    };

    const getValidation = (name, index, value) => {
        const column = props.children.find(c => c.props.name === name);
        if (!column.props.onValidate) return null;

        const result = column.props.onValidate(value);
        if (!result || !result.message) return null;

        const textClassName = result.level === 'information' ? 'text-primary' : result.level === 'warning' ? 'text-warning' : result.level === 'error' ? 'text-danger' : undefined;

        return (<div id={`${props.id}-${name}-${index}-validation-messages`} className={getClassNames(textClassName, 'field-validation')}>
            {result.message}
        </div>)
    };

    const handleOnKeyDown = (e) => {
        if (enableUpDownLineNavigation && props.upDownNavigation && (e.code === 'ArrowUp' || e.code === 'ArrowDown')) {
            e.preventDefault();
            
            const targetIdAttributeName = e.code === 'ArrowUp' ? UP_NAV_ID_ATTRIBUTE_NAME : DOWN_NAV_ID_ATTRIBUTE_NAME;
            const targetIdAttribute = e.target.attributes[targetIdAttributeName];
            if (targetIdAttribute) {
                const targetElement = document.getElementById(targetIdAttribute.value);
                if (targetElement) {
                    targetElement.focus();
                } 
            }
        }
    };

    const getInputType = (name, index, value) => {
        const column = props.children.find(c => c.props.name === name);
        const id = `${props.id}-${index}-${name}-input`;
        const upId = index !== 0 ? `${props.id}-${index - 1}-${name}-input` : '';
        const downId = `${props.id}-${index + 1}-${name}-input`;

        value = applyTransformIfApplicable(name, value);

        const additonalAttributes = {
            [UP_NAV_ID_ATTRIBUTE_NAME]: upId,
            [DOWN_NAV_ID_ATTRIBUTE_NAME]: downId
        };

        if (column.props.type === 'number') {
            return <Input className={column.props.className} 
                id={id} type="number" placeholder={column.props.placeholder} 
                value={value} onChange={(v) => handleOnChange(name, index, v)}
                onKeyDown={handleOnKeyDown}
                additonalAttributes={additonalAttributes} />
        }

        if (column.props.type === 'text') {
            return <Input className={column.props.className} id={id} 
                value={value} placeholder={column.props.placeholder} 
                onChange={(v) => handleOnChange(name, index, v)}
                onKeyDown={handleOnKeyDown}
                additonalAttributes={additonalAttributes} />
        }

        if (column.props.type === 'date') {
            return <Input className={column.props.className} preservePlaceholder={true} 
                id={id} value={value} type="date" 
                placeholder={column.props.placeholder} onChange={(v) => handleOnChange(name, index, v)}
                onKeyDown={handleOnKeyDown}
                additonalAttributes={additonalAttributes} />
        }

        if (column.props.type === 'select') {
            return (<Select className={column.props.className} id={id} value={value} onChange={(v) => handleOnChange(name, index, v)}>
                {column.props.options.map(o => {
                    return <option value={o.value}>{o.text}</option>;
                })}
            </Select>)
        }

        if (column.props.type === 'label') {
            return (<div className={getClassNames('mt-2', column.props.className)} id={id}>
                {value}
            </div>)
        }
    };

    const getRunningTotal = () => {
        if (!enableBillingRunningTotal) return null;

        if (!props.runningTotal || !props.runningTotal.name) return null;

        const column = props.children.find(c => c.props.name === props.runningTotal.name);
        let invalidValueFound = false;
        const parser = props.runningTotal.parser || ((value) => {
            if (isNaN(value)) {
                invalidValueFound = true;
                console.warn(`${value} is not a number and will be assumed to be 0 for the purposes of keeping a running total`);
                return 0;
            }
            return Number(value);
        });

        const total = props.collection.values.reduce((runningTotal, v) => parser(v[props.runningTotal.name]) + runningTotal, 0);
        const label = props.runningTotal.label || `Total ${column.props.label}`;
        return (<div className={getClassNames('col-auto text-end', props.runningTotal.className)}>
            {label}: {props.runningTotal.formatter ? props.runningTotal.formatter(total) : total}
            {invalidValueFound && <div className="text-warning">A non-number value was found.  This value may not be accurate.</div>}
        </div>);
    }

    return (
        <>
            {!props.disableMaxWarningMessage && displayMaxWarning && <div className="row">
                <div className="col">
                    <span className="text-warning">{props.maxWarningMessage || 'Maximum number of lines reached'}</span>
                </div>
            </div>}
            <div className="row justify-content-between align-items-center">
                <div className="col-auto">
                    <Input type="number" label={props.lineCountLabel} labelClassName="fw-bold" id={`${props.id}-total-values`} value={enteredCount} onChange={handleOnEnteredCountChange} />
                </div>
                {getRunningTotal()}
            </div>
            <div className="table-responsive" id={`${props.id}-container`}>
                <table className="table" id={`${props.id}-table`}>
                    <thead>
                        <tr>
                            {props.children}
                        </tr>
                    </thead>
                    <tbody>
                        {props.collection.values.map((v, index) => {
                            const keys = Object.keys(v);
                            return (<tr key={`${index}-row`}>
                                {keys.map(k => {
                                    return (<td key={`${index}-${k}-column`}>
                                        {getInputType(k, index, v[k])}
                                        {getValidation(k, index, v[k])}
                                    </td>);
                                })}
                            </tr>)
                        })}
                    </tbody>
                </table>
            </div>
            <br />
            <div className="row my-1 justify-content-between g-0 me-3">
                <div className="col-auto">
                    <Button className="btn-small btn-dark my-2"
                        id={`${props.id}-add-button`}
                        onClick={handleOnAdd}
                        disabled={reachedMax()}
                        text="Add"
                        icon={<i className="bi bi-plus ms-1"></i>}
                    />
                </div>
                <div className="col-auto">
                    <Button className="btn-small btn-danger my-2"
                        id={`${props.id}-remove-button`}
                        onClick={handleOnRemove}
                        disabled={props.collection.values.length === 0}
                        text="Delete"
                        icon={<i className="bi bi-trash ms-1"></i>}
                    />
                </div>
            </div>
        </>
    )
}